import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { AiOutlineClose } from 'react-icons/ai';
import clsx from 'clsx';
import StepTabs from './StepTabs';
import StepperFooter from './StepperFooter';
import { StepConfig, StepperFooterContext, StepperTheme } from './types';
import { StepperNavMode, StepperNavSource } from '../../../enums/stepperEnums';
import styles from './StepperFormModal.module.scss';

export interface StepperFormModalProps<TStepId extends string> {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Must be referentially stable — declare it at module level. */
  steps: readonly StepConfig<TStepId>[];
  /** Runs from the last step's action button. */
  onConfirm: () => void;
  /** Step to open on; the edit flow points this at the summary. */
  initialStep?: TStepId;
  /** Pass with onStepChange to drive the step from outside. */
  step?: TStepId;
  onStepChange?: (stepId: TStepId) => void;
  navMode?: StepperNavMode;
  /** Route a backdrop click through onClose, e.g. to raise the same exit guard. */
  maskClosable?: boolean;
  isSubmitting?: boolean;
  renderFooter?: (context: StepperFooterContext<TStepId>) => React.ReactNode;
  theme?: StepperTheme;
  width?: number | string;
  className?: string;
}

const DEFAULT_BACK_LABEL = 'BACK';
const DEFAULT_NEXT_LABEL = 'CONTINUE';
const DEFAULT_CONFIRM_LABEL = 'CONFIRM';

const StepperFormModal = <TStepId extends string>({
  open,
  onClose,
  title,
  steps,
  onConfirm,
  initialStep,
  step: controlledStep,
  onStepChange,
  navMode = StepperNavMode.FREE,
  maskClosable = false,
  isSubmitting = false,
  renderFooter,
  theme = 'dark',
  width = 700,
  className,
}: StepperFormModalProps<TStepId>) => {
  const firstStepId = steps[0].id;
  const [internalStep, setInternalStep] = useState<TStepId>(initialStep ?? firstStepId);
  const [visitedIndex, setVisitedIndex] = useState(0);
  const navigationPending = useRef(false);
  const activeStepId = controlledStep ?? internalStep;

  // A step id from outside the config would otherwise index past the array.
  const activeIndex = Math.max(
    steps.findIndex((each) => each.id === activeStepId),
    0,
  );
  const activeStep = steps[activeIndex];
  const isFirstStep = activeIndex === 0;
  const isLastStep = activeIndex === steps.length - 1;

  // Derived rather than stored, so a step driven in from outside counts as
  // reached without waiting for an effect.
  const furthestIndex = Math.max(visitedIndex, activeIndex);

  // In sequential mode only steps already reached are selectable; the next one
  // opens once Continue has been used to complete the current step.
  const maxReachableIndex = navMode === StepperNavMode.FREE ? steps.length - 1 : furthestIndex;

  const moveToStep = useCallback(
    async (stepId: TStepId, index: number, source: StepperNavSource) => {
      // An async gate leaves the buttons live; without this a double click
      // would run it twice and advance two steps.
      if (navigationPending.current) return;
      if (index > activeIndex && activeStep.onBeforeLeave) {
        navigationPending.current = true;
        try {
          if ((await activeStep.onBeforeLeave(stepId, source)) === false) return;
        } finally {
          navigationPending.current = false;
        }
      }
      setVisitedIndex((furthest) => Math.max(furthest, index));
      if (controlledStep === undefined) setInternalStep(stepId);
      onStepChange?.(stepId);
    },
    [activeIndex, activeStep, controlledStep, onStepChange],
  );

  // The guarded entry point: what the tabs and the step components get.
  const goToStep = useCallback(
    (stepId: TStepId) => {
      const targetIndex = steps.findIndex((each) => each.id === stepId);
      if (targetIndex < 0 || targetIndex > maxReachableIndex) return;
      void moveToStep(stepId, targetIndex, StepperNavSource.JUMP);
    },
    [maxReachableIndex, moveToStep, steps],
  );

  // Back and Continue move by one and are what unlock the next step, so they
  // bypass the reachability guard rather than being bound by it.
  const handleBack = useCallback(
    () => void moveToStep(steps[activeIndex - 1].id, activeIndex - 1, StepperNavSource.FOOTER),
    [activeIndex, moveToStep, steps],
  );

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onConfirm();
      return;
    }
    void moveToStep(steps[activeIndex + 1].id, activeIndex + 1, StepperNavSource.FOOTER);
  }, [activeIndex, isLastStep, moveToStep, onConfirm, steps]);

  // The step lives above the Modal, so destroyOnClose won't reset it. Reopening
  // must also forget how far the last run got, hence the direct reset.
  const handleAfterClose = useCallback(() => {
    const resetStepId = initialStep ?? firstStepId;
    setVisitedIndex(Math.max(steps.findIndex((each) => each.id === resetStepId), 0));
    if (controlledStep === undefined) setInternalStep(resetStepId);
    onStepChange?.(resetStepId);
  }, [controlledStep, firstStepId, initialStep, onStepChange, steps]);

  const footerContext = useMemo<StepperFooterContext<TStepId>>(
    () => ({ activeStepId, isFirstStep, isLastStep, isSubmitting, goBack: handleBack, goNext: handleNext }),
    [activeStepId, handleBack, handleNext, isFirstStep, isLastStep, isSubmitting],
  );

  const { Component: ActiveStepComponent } = activeStep;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterClose={handleAfterClose}
      width={width}
      centered
      maskClosable={maskClosable}
      footer={null}
      closeIcon={null}
      rootClassName={styles.stepperFormModalRoot}
      className={clsx(styles.stepperFormModal, styles[theme], className)}
      destroyOnClose
    >
      <div className={styles.stepperForm}>
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
          <AiOutlineClose />
        </button>
        <StepTabs steps={steps} activeStepId={activeStepId} maxReachableIndex={maxReachableIndex} onSelect={goToStep} />
        <h2 className={styles.title}>{title}</h2>
        <div className={styles.body}>
          <ActiveStepComponent goToStep={goToStep} />
        </div>
        {renderFooter ? (
          renderFooter(footerContext)
        ) : (
          <StepperFooter
            {...footerContext}
            hideBack={activeStep.hideBack}
            backLabel={activeStep.backLabel ?? DEFAULT_BACK_LABEL}
            nextLabel={activeStep.nextLabel ?? (isLastStep ? DEFAULT_CONFIRM_LABEL : DEFAULT_NEXT_LABEL)}
          />
        )}
      </div>
    </Modal>
  );
};

export default StepperFormModal;
